import React, { useState } from 'react';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../imageService';
import classNames from 'classnames';
import sgmService from '../../../services/sgmService';

const QuestionComponent = ({ onClose }) => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);

  const sanitizeName = (v) => (v || '').replace(/[^A-Za-zА-Яа-яІЇії’ \-]/g, '');
  const sanitizeEmail = (v) => (v || '').replace(/[<>\/\\"' ;(){}\[\]=`]/g, '');

  const handleNameChange = (e) => {
    const value = sanitizeName(e.target.value);
    setName(value);
  };

  const handleEmailChange = (e) => {
    const value = sanitizeEmail(e.target.value);
    setEmail(value);
  };

  const clearForm = () => {
    setName('');
    setNameError(false);
    setEmail('');
    setEmailError(false);
    setQuestion('');
  };

  const handleClose = () => {
    clearForm();
    onClose();
  };

  const handleCheckEmail = (emailValue = null) => {
    const currentEmail = emailValue?.length > 0 ? emailValue : email;
    if (currentEmail?.length === 0) {
      setEmailError(false);
      return true;
    }

    if (
      currentEmail?.length > 320 ||
      currentEmail.includes('..') ||
      currentEmail.startsWith('.') ||
      currentEmail.endsWith('.') ||
      currentEmail.includes('.@') ||
      currentEmail.includes('@.')
    ) {
      setEmailError(true);
      return false;
    }

    const forbiddenCharsRegex = /[\s\(\)\[\]\{\}<>:"'#$%&*+=?^`|~]/;
    if (forbiddenCharsRegex.test(currentEmail)) {
      setEmailError(true);
      return false;
    }

    const parts = currentEmail.split('@');
    if (parts.length !== 2) {
      setEmailError(true);
      return false;
    }

    const [localPart, domainPart] = parts;

    if (localPart.length < 1 || localPart.length > 64) {
      setEmailError(true);
      return false;
    }

    if (domainPart.length < 3 || domainPart.length > 253) {
      setEmailError(true);
      return false;
    }

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (currentEmail?.length > 0 && !emailRegex.test(currentEmail))
      setEmailError(true);

    return currentEmail?.length > 0 ? emailRegex.test(currentEmail) : true;
  };

  const handleSubmit = async () => {
    if (loading) return;
    let hasError = false;

    if (!name.trim()) {
      setNameError(true);
      hasError = true;
    }

    if (!email.trim() || !handleCheckEmail(email)) {
      setEmailError(true);
      hasError = true;
    }

    if (!question.trim()) {
      hasError = true;
    }

    if (hasError) return;

    setLoading(true);
    try {
      await sgmService.createFeedback(name, email, question);
      handleClose();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <img
        src={getImageFromS3('/images/mgm/bg.png')}
        alt="background"
        className={styles.backgroundImage}
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <svg
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={handleClose}
          >
            <path
              d="M4.29675 8.59375L9.66769 13.9647C10.261 14.5579 10.261 15.5198 9.66769 16.1131C9.07444 16.7063 8.11258 16.7063 7.51932 16.1131L0.707108 9.30086C0.316583 8.91033 0.316583 8.27717 0.707107 7.88664L7.51932 1.07443C8.11258 0.481175 9.07444 0.481175 9.66769 1.07443C10.261 1.66769 10.261 2.62955 9.66769 3.22281L4.29675 8.59375Z"
              fill="white"
            />
            <rect
              x="2.78076"
              y="6.99902"
              width="17"
              height="3"
              rx="1.5"
              fill="white"
            />
          </svg>
          <div style={{ opacity: 0.3 }}>
            Є питання?
            <svg
              width="27"
              height="21"
              viewBox="0 0 27 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.86207 0C0.837931 0 0 0.821739 0 1.82609V16.4348L2.7931 13.6957H20.4828C21.5069 13.6957 22.3448 12.8739 22.3448 11.8696V1.82609C22.3448 0.821739 21.5069 0 20.4828 0H1.86207ZM23.2759 7.30435V11.8696C23.2759 13.4217 22.0655 14.6087 20.4828 14.6087H13.0345V16.4348C13.0345 17.4391 13.8724 18.2609 14.8966 18.2609H24.2069L27 21V9.13043C27 8.12609 26.1621 7.30435 25.1379 7.30435H23.2759Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <h1>Задайте ваше питання</h1>
        <h2>
          Будь ласка, заповніть форму, щоб ми знали, куда відправити відповідь.
        </h2>
        <div className={styles.form}>
          <div className={styles.inputContainer}>
            <p className={styles.inputLabel}>Ім'я</p>
            <input
              type="text"
              placeholder="Введи своє ім'я"
              value={name}
              onChange={handleNameChange}
              onFocus={() => setNameError(false)}
              className={classNames(nameError && styles.errorInput)}
            />
          </div>
          <div className={styles.inputContainer}>
            <p className={styles.inputLabel}>Email</p>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={handleEmailChange}
              onFocus={() => setEmailError(false)}
              onBlur={() => handleCheckEmail()}
              className={classNames(emailError && styles.errorInput)}
            />
            {emailError && (
              <p className={styles.error}>
                Перевір коректність вказаного email
              </p>
            )}
          </div>
          <div className={styles.inputContainer}>
            <p className={styles.inputLabel}>Введіть питання</p>
            <textarea
              placeholder="Введіть питання"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <button
            className={styles.sendButton}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Відправка...' : 'Відправити'}
          </button>
          <button className={styles.closeButton} onClick={handleClose}>
            Закрити
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionComponent;
